export const STAGE_IDS = [
  "notice",
  "play",
  "predict",
  "name",
  "solve",
  "explain",
  "transfer",
] as const;

export type StageId = (typeof STAGE_IDS)[number];
export type JourneyId = `ALG-${string}`;
export type VisibleState =
  | "hidden"
  | "discovered"
  | "working"
  | "strong"
  | "remembered";

export interface EvidenceVector {
  intuition: number;
  manipulation: number;
  symbolic: number;
  explanation: number;
  transfer: number;
  retention: number;
}

export interface MisconceptionRecord {
  code: string;
  status: "candidate" | "active" | "resolved";
  confidence: number;
  basis: string[];
}

export interface ResumeCheckpoint {
  stage: StageId;
  maxStage: number;
  session: Record<string, unknown>;
  savedAt: string;
}

export interface ConceptProjection {
  visibleState: VisibleState;
  stage: StageId;
  evidence: EvidenceVector;
  representationsSucceeded: string[];
  recentIndependentCorrect: number;
  misconceptions: MisconceptionRecord[];
  resumeCheckpoint: ResumeCheckpoint | null;
}

export interface ReviewItem {
  conceptId: JourneyId;
  dueAt: string;
  variantId: string;
  reason?: string;
}

export interface LearnerProjectionV1 {
  learnerId: string;
  projectionVersion: "1.0.0";
  revision: number;
  asOfEventId: string | null;
  concepts: Record<string, ConceptProjection>;
  reviewQueue: ReviewItem[];
}

export type LearningEventType =
  | "concept_started"
  | "stage_entered"
  | "widget_action"
  | "prediction_submitted"
  | "question_presented"
  | "answer_submitted"
  | "hint_requested"
  | "hint_presented"
  | "misconception_candidate"
  | "remediation_started"
  | "remediation_completed"
  | "explanation_submitted"
  | "transfer_gate_completed"
  | "concept_state_changed"
  | "review_scheduled"
  | "review_completed"
  | "session_ended";

export interface LearningEventV1 {
  eventId: string;
  schemaVersion: "1.0.0";
  occurredAt: string;
  sessionId: string;
  learnerId: string;
  conceptId: JourneyId;
  conceptVersion: string;
  questionTemplateId?: string | null;
  questionTemplateVersion?: string | null;
  questionSeed?: string | null;
  widgetId?: string | null;
  type: LearningEventType;
  payload: Record<string, unknown>;
  clientSequence: number;
  idempotencyKey: string;
  deviceId: string;
}

export interface ChoiceDefinition {
  id: string;
  label: string;
  correct: boolean;
  feedback: string;
}

export interface SolveDefinition {
  id: string;
  questionFamilyId: string;
  representation: "box" | "reversed" | "story" | "linked-boxes";
  equation?: string;
  story?: string;
  prompt: string;
  known: number;
  target: number;
  coefficient: number;
  answer: number;
  success: string;
  hints: string[];
}

export interface JourneyDefinitionV1 {
  schemaVersion: "1.0.0";
  id: JourneyId;
  version: string;
  number: number;
  title: string;
  world: string;
  glyph: string;
  bigIdea: string;
  artifactKind: string;
  prerequisites: JourneyId[];
  stages: {
    notice: {
      instruction: string;
      equation: string;
      question: string;
      choices: ChoiceDefinition[];
    };
    play: {
      instruction: string;
      known: number;
      target: number;
      start: number;
      max: number;
      success: string;
    };
    predict: {
      instruction: string;
      question: string;
      choices: ChoiceDefinition[];
    };
    name: {
      instruction: string;
      term: string;
      explanation: string;
    };
    solve: {
      instruction: string;
      questions: SolveDefinition[];
    };
    explain: {
      instruction: string;
      equation: string;
      question: string;
      choices: ChoiceDefinition[];
    };
    transfer: {
      instruction: string;
      question: SolveDefinition;
    };
  };
  mastery: {
    symbolic: number;
    explanation: number;
    transfer: number;
    independentCorrect: number;
    representations: number;
  };
}

export interface JourneyCatalogEntry {
  id: JourneyId;
  number: number;
  title: string;
  world: string;
  glyph: string;
  summary: string;
  region: "first-trail" | "new-horizons";
  implementation: "live" | "migration-pending";
}

export interface LocalIdentity {
  learnerId: string;
  deviceId: string;
}

export interface AppendResult {
  projection: LearnerProjectionV1;
  acceptedEventIds: string[];
}

export interface PersistencePort {
  getOrCreateIdentity(): Promise<LocalIdentity>;
  loadProjection(learnerId: string): Promise<LearnerProjectionV1>;
  appendEvents(events: LearningEventV1[]): Promise<AppendResult>;
  saveCheckpoint(
    learnerId: string,
    conceptId: JourneyId,
    checkpoint: ResumeCheckpoint,
  ): Promise<LearnerProjectionV1>;
  listEvents(learnerId: string, limit?: number): Promise<LearningEventV1[]>;
  acknowledgeSync(eventIds: string[]): Promise<void>;
  resetLearner(learnerId: string): Promise<void>;
}

export interface SyncRequestV1 {
  learnerId: string;
  deviceId: string;
  baseRevision: number;
  events: LearningEventV1[];
}

export interface SyncResponseV1 {
  acceptedEventIds: string[];
  revision: number;
  projection: LearnerProjectionV1;
}
